import { signal, computed, effect } from "@preact/signals-core";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BrandCutoffDate {
  brand_name: string;
  holiday_shipping_cutoff_date: string;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
}

// ─── Configuration ────────────────────────────────────────────────────────────

/** Whether holiday shipping feature is enabled (from theme settings) */
export const isHolidayShippingEnabled = signal<boolean>(false);

/** Global fallback cutoff date (from theme settings) */
export const globalCutoffDate = signal<string | null>(null);

/** Brand-specific cutoff dates (from shop metafield JSON) */
export const brandCutoffDates = signal<Record<string, string>>({});

/** Gift card product URL */
export const giftCardUrl = signal<string>("/products/gift-card");

// ─── Runtime State ────────────────────────────────────────────────────────────

/** Current product's cutoff date (calculated from product → collection → global) */
export const productCutoffDate = signal<string | null>(null);

/** Current product's cutoff timestamp */
export const productCutoffTimestamp = computed(() => {
  const cutoff = productCutoffDate.value;
  if (!cutoff) return null;
  return new Date(cutoff).getTime();
});

/** Whether the cutoff has expired */
export const hasExpired = computed(() => {
  const timestamp = productCutoffTimestamp.value;
  if (!timestamp) return false;
  return new Date().getTime() > timestamp;
});

/** Time remaining until cutoff (null if expired or no cutoff) */
export const timeRemaining = signal<TimeRemaining | null>(null);

// ─── Internal State ───────────────────────────────────────────────────────────

let countdownInterval: number | null = null;

// ─── Initialization ───────────────────────────────────────────────────────────

/**
 * Initialize holiday shipping system with theme settings and metafield data.
 * Should be called once per page load from Liquid template.
 */
export const initHolidayShipping = (
  enabled: boolean,
  globalCutoff: string | null,
  brandsJson: BrandCutoffDate[] | null,
  giftCardProductUrl: string = "/products/gift-card"
) => {
  isHolidayShippingEnabled.value = enabled;
  globalCutoffDate.value = globalCutoff;
  giftCardUrl.value = giftCardProductUrl;

  // Parse brands JSON into lookup object
  if (brandsJson && Array.isArray(brandsJson)) {
    const brandMap: Record<string, string> = {};
    brandsJson.forEach((brand) => {
      if (brand.brand_name && brand.holiday_shipping_cutoff_date) {
        brandMap[brand.brand_name] = brand.holiday_shipping_cutoff_date;
      }
    });
    brandCutoffDates.value = brandMap;
  }

  // Start countdown
  startCountdown();
};

// ─── Product Cutoff Calculation ──────────────────────────────────────────────

/**
 * Set the cutoff date for the current product.
 * Priority: product metafield → collection brand → global
 */
export const setProductCutoff = (product: any) => {
  if (!isHolidayShippingEnabled.value) {
    productCutoffDate.value = null;
    return;
  }

  let cutoff: string | null = null;

  // 1. Check product metafield
  if (product?.metafields?.holiday_shipping?.holiday_shipping_cutoff_date) {
    cutoff = product.metafields.holiday_shipping.holiday_shipping_cutoff_date;
  }

  // 2. Check collections for brand match
  if (!cutoff && product?.collections && Array.isArray(product.collections)) {
    for (const collection of product.collections) {
      const handle = collection.handle;
      if (handle && brandCutoffDates.value[handle]) {
        cutoff = brandCutoffDates.value[handle];
        break;
      }
    }
  }

  // 3. Fallback to global
  if (!cutoff) {
    cutoff = globalCutoffDate.value;
  }

  productCutoffDate.value = cutoff;
};

// ─── Countdown Management ─────────────────────────────────────────────────────

/**
 * Start the countdown interval (updates every minute).
 */
export const startCountdown = () => {
  // Clear any existing interval
  stopCountdown();

  // Update immediately
  updateCountdown();

  // Update every minute (60000ms)
  countdownInterval = window.setInterval(() => {
    updateCountdown();
  }, 60000);
};

/**
 * Stop the countdown interval.
 */
export const stopCountdown = () => {
  if (countdownInterval !== null) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
};

/**
 * Update the time remaining calculation.
 */
const updateCountdown = () => {
  const timestamp = productCutoffTimestamp.value;

  if (!timestamp) {
    timeRemaining.value = null;
    return;
  }

  const now = new Date().getTime();
  const distance = timestamp - now;

  if (distance < 0) {
    timeRemaining.value = null;
    return;
  }

  const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

  timeRemaining.value = { days, hours, minutes };
};

// ─── Utility Helpers ──────────────────────────────────────────────────────────

/**
 * Format the cutoff date for display.
 */
export const getFormattedCutoffDate = (): string | null => {
  const cutoff = productCutoffDate.value;
  if (!cutoff) return null;

  const date = new Date(cutoff);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * Get a formatted countdown string (e.g., "2 days, 5 hours, 30 minutes").
 */
export const getFormattedCountdown = (): string | null => {
  const time = timeRemaining.value;
  if (!time) return null;

  const parts: string[] = [];
  if (time.days > 0) parts.push(`${time.days} day${time.days !== 1 ? "s" : ""}`);
  if (time.hours > 0) parts.push(`${time.hours} hour${time.hours !== 1 ? "s" : ""}`);
  if (time.minutes > 0) parts.push(`${time.minutes} minute${time.minutes !== 1 ? "s" : ""}`);

  return parts.join(", ");
};
